import { useState } from "react";
import { Loader2, Lightbulb, Send, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/69c3e51b09d578df3773fc1f5bf83b8908cfaee3362d0b20a018b9936a5c6470.png";

const CATS = [
  { key: "improvement", label: "Improvement" },
  { key: "idea", label: "New idea / feature" },
  { key: "bug", label: "Something's broken" },
  { key: "other", label: "Other" },
];

export default function SuggestSite() {
  const [form, setForm] = useState({ name: "", email: "", category: "improvement", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error("Please share your suggestion."); return; }
    setLoading(true);
    try {
      await api.post("/suggestions", { ...form, page_url: window.location.href });
      setDone(true);
      toast.success("Thank you — your suggestion has been received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="suggest-page">
      <PageHero label="Your voice shapes the ecosystem" title="Suggest an improvement"
        subtitle="Have an idea, a request, or spotted something that could be better? Tell us — the ecosystem grows through the people who tend it."
        image={HERO} testid="suggest-hero" />
      <section className="max-w-2xl mx-auto px-6 py-16">
        {done ? (
          <div className="eyw-card p-10 text-center" data-testid="suggest-thanks">
            <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-[#1A242B]">Thank you</h2>
            <p className="text-[#4A5D6B] mt-2">Your suggestion has been received. We read every one.</p>
            <button onClick={() => { setDone(false); setForm({ name: "", email: "", category: "improvement", message: "" }); }} className="eyw-btn-ghost mt-6">Share another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="eyw-card p-8 space-y-4" data-testid="suggest-form">
            <div className="flex items-center gap-2 text-[#144F75]"><Lightbulb size={20} /><span className="font-medium">Tell us what would make this better</span></div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#8FA89B] font-semibold mb-2 block">What kind of feedback is this?</label>
              <div className="flex flex-wrap gap-2" data-testid="suggest-categories">
                {CATS.map((c) => (
                  <button type="button" key={c.key} onClick={() => setForm((f) => ({ ...f, category: c.key }))} data-testid={`suggest-cat-${c.key}`}
                    className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${form.category === c.key ? "bg-[#144F75] text-white border-[#144F75]" : "border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <textarea required rows={5} placeholder="Share your suggestion, idea, or the issue you noticed…" value={form.message} onChange={set("message")} data-testid="suggest-message" className="eyw-input w-full resize-none" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Your name (optional)" value={form.name} onChange={set("name")} data-testid="suggest-name" className="eyw-input w-full" />
              <input type="email" placeholder="Email (optional, if you'd like a reply)" value={form.email} onChange={set("email")} data-testid="suggest-email" className="eyw-input w-full" />
            </div>
            <button type="submit" disabled={loading} data-testid="suggest-submit" className="eyw-btn w-full justify-center disabled:opacity-60">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} /> Send suggestion</>}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
